import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import UserNotice from '../components/UserNotice';
import { copyToClipboard } from '../utils/clipboard';

/* =====================================================================
 * Dashboard do Cliente — Painel Principal
 * ---------------------------------------------------------------------
 *  - Header limpo com avatar + dropdown (mobile-friendly)
 *  - Hero compacto com saudação + saldo
 *  - Insights reais (códigos total, esta semana, pedidos pendentes)
 *  - 2 ações grandes (Gerar / Comprar)
 *  - Preview dos últimos códigos (cópia rápida)
 *  - Aviso de pedido pendente (quando existe)
 *  - Dark mode + mobile responsivo
 * =================================================================== */

// ----- helpers -----
const ROUTE_COLORS = {
  BR:  { bg: 'rgba(34,197,94,0.12)',  color: '#16a34a', label: 'BR' },
  CN:  { bg: 'rgba(239,68,68,0.12)',  color: '#dc2626', label: 'CN' },
  INT: { bg: 'rgba(59,130,246,0.12)', color: '#2563eb', label: 'INT' },
};

const getRoute = (codigo) => {
  if (!codigo) return 'BR';
  if (codigo.endsWith('INT')) return 'INT';
  if (codigo.endsWith('CN'))  return 'CN';
  return 'BR';
};

const formatRelTime = (s) => {
  if (!s) return '';
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString('pt-BR');
};

const greet = () => {
  const h = new Date().getHours();
  if (h < 6)  return 'Boa madrugada';
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
};

const isThisWeek = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T'));
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 86400000);
  return d >= weekAgo;
};

// ----- Componente principal -----
const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  // dados auxiliares
  const [codes, setCodes] = useState([]);          // /tracking/my-codes
  const [orders, setOrders] = useState([]);        // /credits/orders
  const [loadingData, setLoadingData] = useState(true);

  // (Avatar/menu/alterar-senha removidos — agora vivem no ClientLayout/sidebar)

  // copy feedback
  const [copied, setCopied] = useState(null);

  // ---- shopify status + pedidos pendentes ----
  const [shopifyConnected, setShopifyConnected] = useState(false);
  const [shopifyPendingOrders, setShopifyPendingOrders] = useState(0);

  const fetchShopify = useCallback(async () => {
    try {
      // Multi-loja: /status agora retorna um array de lojas, não 1 objeto.
      const statusRes = await api.get('/shopify/status');
      const integrations = statusRes.data?.integrations || [];
      const connected = integrations.some((i) => i.connected);
      setShopifyConnected(connected);
      if (!connected) { setShopifyPendingOrders(0); return; }
      try {
        // Endpoint agregado: soma pendentes de TODAS as lojas em 1 única requisição.
        const pendingRes = await api.get('/shopify/orders/pending-count');
        setShopifyPendingOrders(pendingRes.data?.total || 0);
      } catch (_) {
        setShopifyPendingOrders(0);
      }
    } catch (_) {
      setShopifyConnected(false);
      setShopifyPendingOrders(0);
    }
  }, []);

  // ---- carrega dados ----
  const fetchData = useCallback(async () => {
    try {
      const [codesRes, ordersRes] = await Promise.all([
        api.get('/tracking/my-codes').catch(() => ({ data: [] })),
        api.get('/credits/orders').catch(() => ({ data: [] })),
      ]);
      setCodes(Array.isArray(codesRes.data) ? codesRes.data : []);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
    } catch (e) {
      console.error('Dashboard fetch error', e);
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
    fetchData();
    fetchShopify();
    const onFocus = () => { refreshUser(); fetchData(); fetchShopify(); };
    window.addEventListener('focus', onFocus);
    const id = setInterval(() => { refreshUser(); fetchData(); fetchShopify(); }, 30000);
    return () => { window.removeEventListener('focus', onFocus); clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- handlers ----
  const copyCode = async (codigo) => {
    const ok = await copyToClipboard(codigo);
    if (ok) {
      setCopied(codigo);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  // ---- derived ----
  const credits = user?.credits ?? 0;
  const lowBalance = credits > 0 && credits < 10;
  const noBalance = credits === 0;

  const codesThisWeek = codes.filter(c => isThisWeek(c.created_at)).length;
  const recentCodes = codes.slice(0, 5);  // já vêm ordenados desc do backend
  const pendingOrder = orders.find(o => o.status === 'pending');

  // ----------- RENDER -----------
  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .dash *, .dash *::before, .dash *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .dash {
          font-family: 'Lexend', sans-serif; color: #475569; background: #f6f7fb;
          min-height: 100vh; overflow-x: hidden; -webkit-font-smoothing: antialiased;
        }

        /* ===== HEADER (limpo) ===== */
        .dash-header {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          padding: 14px 28px; display: flex; align-items: center; gap: 14px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 1000;
        }
        .dash-brand {
          color: #fff; font-size: 17px; font-weight: 700; letter-spacing: -0.2px;
          display: flex; align-items: center; gap: 10px;
        }
        .dash-brand i { color: #f97316; font-size: 18px; }
        .dash-header-spacer { flex: 1; }

        /* Saldo badge */
        .dash-balance {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 7px 14px; border-radius: 12px; cursor: pointer;
          background: linear-gradient(135deg, #f97316, #ea580c); color: #fff;
          font-weight: 700; font-size: 14px; border: none;
          box-shadow: 0 4px 14px rgba(249,115,22,0.35);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .dash-balance:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(249,115,22,0.45); }
        .dash-balance i { font-size: 13px; }
        .dash-balance.low {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          box-shadow: 0 4px 14px rgba(239,68,68,0.40);
          animation: dash-pulse-low 2s ease-in-out infinite;
        }
        @keyframes dash-pulse-low { 0%,100% { box-shadow: 0 4px 14px rgba(239,68,68,0.40); } 50% { box-shadow: 0 4px 22px rgba(239,68,68,0.65); } }

        /* Avatar + dropdown */
        .dash-avatar-wrap { position: relative; }
        .dash-avatar {
          width: 38px; height: 38px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.20);
          background: linear-gradient(135deg, #f97316, #ea580c); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 15px; cursor: pointer;
          transition: transform 0.2s, border-color 0.2s;
        }
        .dash-avatar:hover { transform: scale(1.05); border-color: rgba(249,115,22,0.6); }
        .dash-menu {
          position: absolute; top: calc(100% + 10px); right: 0;
          background: #fff; border-radius: 14px; min-width: 220px;
          box-shadow: 0 12px 36px rgba(0,0,0,0.18);
          overflow: hidden; z-index: 2000;
          animation: dash-fade-in 0.18s ease;
        }
        @keyframes dash-fade-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .dash-menu-user {
          padding: 14px 16px; background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
          color: #fff;
        }
        .dash-menu-user .name { font-weight: 700; font-size: 15px; }
        .dash-menu-user .email { font-size: 12px; opacity: 0.85; margin-top: 2px; }
        .dash-menu-item {
          width: 100%; background: none; border: none; padding: 12px 16px;
          text-align: left; cursor: pointer; display: flex; align-items: center; gap: 12px;
          font-family: 'Lexend', sans-serif; font-size: 14px; color: #374151;
          transition: background 0.15s;
        }
        .dash-menu-item:hover { background: #f6f7fb; }
        .dash-menu-item i { width: 18px; text-align: center; color: #94a3b8; }
        .dash-menu-item.danger { color: #dc2626; }
        .dash-menu-item.danger i { color: #dc2626; }
        .dash-menu-divider { height: 1px; background: #f0f1f5; margin: 4px 0; }

        /* ===== MAIN ===== */
        .dash-main { max-width: 1200px; margin: 0 auto; padding: 28px 22px; }

        /* HERO */
        .dash-hero {
          background: linear-gradient(135deg, #0f172a 0%, #1e1330 60%, #0f172a 100%);
          border-radius: 22px; padding: 28px 32px; position: relative; overflow: hidden;
          margin-bottom: 20px;
          border: 1px solid rgba(249,115,22,0.18);
        }
        .dash-hero::before {
          content: ''; position: absolute; inset: 0; pointer-events: none;
          background: radial-gradient(circle at 90% 10%, rgba(249,115,22,0.30), transparent 60%);
        }
        .dash-hero-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: 24px; position: relative; z-index: 1;
        }
        .dash-hero-eyebrow {
          color: rgba(255,255,255,0.65); font-size: 12px; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 4px;
        }
        .dash-hero-title {
          font-size: 26px; font-weight: 800; color: #fff; letter-spacing: -0.4px;
        }
        .dash-hero-title .accent {
          background: linear-gradient(135deg, #fdba74, #fb923c);
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
        }
        .dash-hero-sub { color: rgba(255,255,255,0.70); font-size: 13.5px; margin-top: 4px; }
        .dash-hero-balance {
          text-align: right; padding-left: 24px; border-left: 1px solid rgba(255,255,255,0.10);
          flex-shrink: 0;
        }
        .dash-hero-balance-label {
          font-size: 11px; color: rgba(255,255,255,0.55); font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 4px;
        }
        .dash-hero-balance-value {
          font-size: 38px; font-weight: 800; color: #fdba74; letter-spacing: -1px; line-height: 1;
        }
        .dash-hero-balance-unit { font-size: 12px; color: rgba(255,255,255,0.55); margin-top: 6px; }

        /* Insights (mini stats) */
        .dash-insights {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 14px;
          margin-bottom: 20px;
        }
        .dash-insight {
          background: #fff; border-radius: 14px; padding: 16px 18px;
          display: flex; align-items: center; gap: 14px;
          box-shadow: 0 4px 16px rgba(15,23,42,0.05);
          border: 1px solid rgba(15,23,42,0.04);
          transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s;
          position: relative;
        }
        .dash-insight:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgba(15,23,42,0.08); }
        .dash-insight-icon {
          width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 18px;
          position: relative;
        }
        .dash-insight-icon.codes { background: linear-gradient(135deg, #fed7aa, #fdba74); color: #c2410c; }
        .dash-insight-icon.week  { background: linear-gradient(135deg, #ddd6fe, #c4b5fd); color: #6d28d9; }
        .dash-insight-icon.shopify { background: linear-gradient(135deg, #c7d2fe, #a5b4fc); color: #4338ca; }
        .dash-insight-icon.shopify-done { background: linear-gradient(135deg, #86efac, #4ade80); color: #14532d; }
        .dash-insight-label { font-size: 11.5px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }
        .dash-insight-value { font-size: 20px; font-weight: 800; color: #0f172a; line-height: 1.1; margin-top: 2px; }
        .dash-insight-sub { font-size: 12px; color: #94a3b8; margin-top: 2px; }
        .dash-ok-sub { color: #16a34a; font-weight: 700; display: inline-flex; align-items: center; gap: 4px; }

        /* Card "códigos pendentes" — chama atenção quando há pedidos aguardando */
        .dash-insight.shopify-pending {
          cursor: pointer; border-color: rgba(249,115,22,0.35);
          animation: dash-card-glow 2.2s ease-in-out infinite;
        }
        @keyframes dash-card-glow {
          0%, 100% { box-shadow: 0 4px 16px rgba(15,23,42,0.05); }
          50% { box-shadow: 0 4px 26px rgba(249,115,22,0.38); }
        }
        .dash-insight-dot {
          position: absolute; top: -3px; right: -3px; width: 12px; height: 12px;
          border-radius: 50%; background: #ef4444; border: 2px solid #fff;
          animation: dash-dot-ping 1.6s ease-in-out infinite;
        }
        @keyframes dash-dot-ping { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.35); opacity: 0.7; } }
        .dash-icon-shake { display: inline-block; animation: dash-shake 2.4s ease-in-out infinite; }
        @keyframes dash-shake {
          0%, 80%, 100% { transform: rotate(0deg); }
          82% { transform: rotate(-12deg); }
          85% { transform: rotate(10deg); }
          88% { transform: rotate(-8deg); }
          91% { transform: rotate(6deg); }
          94% { transform: rotate(0deg); }
        }

        /* Card "tudo em dia" — estado positivo, sem pendências */
        .dash-insight.shopify-ok { cursor: pointer; border-color: rgba(34,197,94,0.30); }

        /* Alert banner (pedido pendente / saldo zero) */
        .dash-alert-banner {
          display: flex; align-items: center; gap: 14px;
          padding: 14px 18px; border-radius: 14px; margin-bottom: 20px;
          background: linear-gradient(135deg, #fef3c7, #fde68a);
          border: 1px solid #f59e0b; color: #92400e;
        }
        .dash-alert-banner.danger {
          background: linear-gradient(135deg, #fee2e2, #fecaca); border-color: #ef4444; color: #991b1b;
        }
        .dash-alert-banner i { font-size: 22px; flex-shrink: 0; }
        .dash-alert-banner .txt { flex: 1; font-size: 14px; font-weight: 500; }
        .dash-alert-banner .txt strong { font-weight: 700; }
        .dash-alert-banner button {
          padding: 9px 16px; border-radius: 10px; border: none; cursor: pointer;
          background: #92400e; color: #fff; font-family: 'Lexend', sans-serif;
          font-size: 13px; font-weight: 700; transition: all 0.2s; white-space: nowrap;
        }
        .dash-alert-banner button:hover { transform: translateY(-1px); }
        .dash-alert-banner.danger button { background: #991b1b; }



        /* Actions (2 grandes) */
        .dash-actions {
          display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 22px;
        }
        .dash-action-card {
          background: #fff; border-radius: 18px; padding: 24px 26px;
          cursor: pointer; transition: all 0.22s; position: relative; overflow: hidden;
          box-shadow: 0 6px 22px rgba(15,23,42,0.06);
          border: 1px solid rgba(15,23,42,0.05); text-align: left;
          display: flex; align-items: center; gap: 18px;
        }
        .dash-action-card:hover { transform: translateY(-4px); box-shadow: 0 14px 36px rgba(15,23,42,0.12); }
        .dash-action-icon {
          width: 60px; height: 60px; border-radius: 16px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 26px;
          color: #fff;
        }
        .dash-action-card.primary .dash-action-icon {
          background: linear-gradient(135deg, #f97316, #ea580c);
          box-shadow: 0 8px 22px rgba(249,115,22,0.40);
        }
        .dash-action-card.secondary .dash-action-icon {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          box-shadow: 0 8px 22px rgba(59,130,246,0.40);
        }
        .dash-action-body { flex: 1; min-width: 0; }
        .dash-action-title {
          font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -0.3px;
        }
        .dash-action-desc {
          color: #64748b; font-size: 13px; margin-top: 4px; line-height: 1.45;
        }
        .dash-action-arrow {
          color: #94a3b8; font-size: 16px; transition: transform 0.2s, color 0.2s;
        }
        .dash-action-card:hover .dash-action-arrow {
          transform: translateX(4px); color: #f97316;
        }
        .dash-action-card.primary:hover .dash-action-arrow { color: #f97316; }
        .dash-action-card.secondary:hover .dash-action-arrow { color: #3b82f6; }

        /* ===== INTEGRATIONS ===== */
        .dash-section-title {
          font-size: 13px; font-weight: 700; color: #94a3b8;
          text-transform: uppercase; letter-spacing: 0.6px;
          margin: 6px 4px 12px; display: flex; align-items: center; gap: 10px;
        }
        .dash-section-title::after {
          content: ''; flex: 1; height: 1px; background: rgba(15,23,42,0.07);
        }
        .dash-integrations {
          display: grid; grid-template-columns: 1fr; gap: 14px; margin-bottom: 22px;
        }
        .dash-integration-card {
          background: #fff; border-radius: 18px; padding: 20px 22px;
          cursor: pointer; transition: all 0.22s; position: relative; overflow: hidden;
          box-shadow: 0 6px 22px rgba(15,23,42,0.06);
          border: 1px solid rgba(15,23,42,0.05); text-align: left;
          display: flex; align-items: center; gap: 16px;
        }
        .dash-integration-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 14px 36px rgba(15,23,42,0.12);
          border-color: rgba(149,191,76,0.30);
        }
        .dash-integration-card::before {
          content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px;
          background: linear-gradient(180deg, #95BF47 0%, #5E8E3E 100%);
        }
        .dash-integration-icon {
          width: 54px; height: 54px; border-radius: 14px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #95BF47 0%, #5E8E3E 100%);
          color: #fff; font-size: 26px;
          box-shadow: 0 6px 18px rgba(94,142,62,0.35);
        }
        .dash-integration-body { flex: 1; min-width: 0; }
        .dash-integration-title {
          font-size: 16px; font-weight: 800; color: #0f172a;
          display: flex; align-items: center; gap: 10px; letter-spacing: -0.2px;
        }
        .dash-integration-badge {
          font-size: 10px; font-weight: 800; padding: 3px 8px; border-radius: 8px;
          background: linear-gradient(135deg, #95BF47, #5E8E3E); color: #fff;
          letter-spacing: 0.5px; text-transform: uppercase;
        }
        .dash-integration-badge.connected {
          background: linear-gradient(135deg, #22c55e, #16a34a);
        }
        .dash-integration-desc {
          color: #64748b; font-size: 13px; margin-top: 4px; line-height: 1.45;
        }
        .dash-integration-card .dash-action-arrow { color: #94a3b8; }
        .dash-integration-card:hover .dash-action-arrow {
          transform: translateX(4px); color: #5E8E3E;
        }

        /* Panel (códigos recentes) */
        .dash-panel {
          background: #fff; border-radius: 18px; padding: 22px 24px;
          box-shadow: 0 4px 18px rgba(15,23,42,0.05);
          border: 1px solid rgba(15,23,42,0.04);
        }
        .dash-panel-head {
          display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;
        }
        .dash-panel-title {
          font-size: 16px; font-weight: 700; color: #0f172a;
          display: flex; align-items: center; gap: 10px;
        }
        .dash-panel-title i { color: #f97316; font-size: 16px; }
        .dash-panel-link {
          background: none; border: none; color: #f97316; font-weight: 700;
          font-size: 13px; cursor: pointer; font-family: 'Lexend', sans-serif;
          display: flex; align-items: center; gap: 4px; transition: gap 0.2s;
        }
        .dash-panel-link:hover { gap: 8px; }

        .dash-codes-list { display: flex; flex-direction: column; gap: 8px; }
        .dash-code-row {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 14px; background: #f6f7fb; border-radius: 12px;
          transition: background 0.18s;
        }
        .dash-code-row:hover { background: #eef0f7; }
        .dash-code-route {
          font-size: 11px; font-weight: 800; padding: 3px 8px; border-radius: 8px;
          flex-shrink: 0; letter-spacing: 0.4px;
        }
        .dash-code-text { flex: 1; min-width: 0; }
        .dash-code-num {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 13.5px; font-weight: 700; color: #0f172a; letter-spacing: 0.3px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .dash-code-meta { font-size: 11.5px; color: #94a3b8; margin-top: 2px; }
        .dash-code-copy {
          background: #fff; border: 1px solid #e5e7eb; border-radius: 10px;
          width: 36px; height: 36px; cursor: pointer; color: #64748b;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.18s; flex-shrink: 0;
        }
        .dash-code-copy:hover { background: #f97316; color: #fff; border-color: #f97316; }
        .dash-code-copy.copied { background: #22c55e; color: #fff; border-color: #22c55e; }

        .dash-empty {
          padding: 32px 20px; text-align: center; color: #94a3b8;
        }
        .dash-empty i { font-size: 32px; color: #cbd5e1; margin-bottom: 8px; display: block; }
        .dash-empty-text { font-size: 14px; }
        .dash-empty-cta {
          margin-top: 14px; padding: 10px 20px; border-radius: 10px; border: none;
          background: linear-gradient(135deg, #f97316, #ea580c); color: #fff;
          font-weight: 700; cursor: pointer; font-family: 'Lexend', sans-serif;
          font-size: 13px; box-shadow: 0 4px 14px rgba(249,115,22,0.35);
        }

        /* Skeletons */
        .dash-skel { background: linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%); background-size: 200% 100%; animation: dash-skel 1.4s ease-in-out infinite; border-radius: 10px; }
        @keyframes dash-skel { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        .dash-skel-row { height: 48px; margin-bottom: 8px; }

        /* ===== MOBILE ===== */
        @media (max-width: 1024px) {
          .dash-insights { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 820px) {
          .dash-insights { grid-template-columns: 1fr 1fr; }
          .dash-actions { grid-template-columns: 1fr; }
          .dash-hero-balance-value { font-size: 32px; }
        }
        @media (max-width: 560px) {
          .dash-header { padding: 12px 16px; gap: 8px; }
          .dash-brand { font-size: 15px; }
          .dash-brand .brand-text { display: none; }
          .dash-balance { padding: 6px 11px; font-size: 13px; }
          .dash-main { padding: 20px 14px; }
          .dash-hero { padding: 22px 22px; }
          .dash-hero-row { flex-direction: column; align-items: flex-start; gap: 18px; }
          .dash-hero-balance { border-left: none; border-top: 1px solid rgba(255,255,255,0.10); padding-left: 0; padding-top: 16px; text-align: left; width: 100%; }
          .dash-hero-title { font-size: 22px; }
          .dash-insights { grid-template-columns: 1fr; }
          .dash-action-card { padding: 18px 20px; }
          .dash-action-icon { width: 48px; height: 48px; font-size: 22px; }
          .dash-action-title { font-size: 16px; }
          .dash-alert-banner { flex-direction: column; align-items: stretch; text-align: center; }
          .dash-alert-banner button { width: 100%; }
        }
      `}</style>

      <UserNotice />

      <div className="dash">
        {/* Cabeçalho antigo (saldo + avatar com menu) removido — tudo isso
            já está na sidebar do ClientLayout (saldo, tema, alterar senha,
            sair, painel admin). Evita duplicação de UI. */}

        <main className="dash-main">
          {/* HERO */}
          <section className="dash-hero">
            <div className="dash-hero-row">
              <div>
                <div className="dash-hero-eyebrow">{greet()}</div>
                <h1 className="dash-hero-title">
                  Olá, <span className="accent">{user?.username}</span> 👋
                </h1>
                <p className="dash-hero-sub">
                  {noBalance
                    ? 'Você está sem créditos. Compre para começar a gerar códigos.'
                    : lowBalance
                    ? 'Seu saldo está acabando. Considere comprar mais créditos.'
                    : 'Pronto para gerar mais códigos de rastreamento?'}
                </p>
              </div>
              <div className="dash-hero-balance">
                <div className="dash-hero-balance-label">Saldo atual</div>
                <div className="dash-hero-balance-value">{credits}</div>
                <div className="dash-hero-balance-unit">crédito{credits !== 1 ? 's' : ''} disponíve{credits !== 1 ? 'is' : 'l'}</div>
              </div>
            </div>
          </section>

          {/* ALERTAS */}
          {pendingOrder && (
            <div className="dash-alert-banner">
              <i className="fas fa-clock"></i>
              <div className="txt">
                Você tem um pedido <strong>aguardando pagamento</strong> ({pendingOrder.quantity} créditos).
              </div>
              <button onClick={() => navigate('/buy-credits')}>Ver detalhes</button>
            </div>
          )}

          {noBalance && !pendingOrder && (
            <div className="dash-alert-banner danger">
              <i className="fas fa-exclamation-triangle"></i>
              <div className="txt">
                <strong>Sem créditos.</strong> Para gerar códigos você precisa comprar créditos primeiro.
              </div>
              <button onClick={() => navigate('/buy-credits')}>Comprar agora</button>
            </div>
          )}

          {/* INSIGHTS */}
          <div className="dash-insights">
            <div className="dash-insight">
              <div className="dash-insight-icon codes">
                <i className="fas fa-qrcode"></i>
              </div>
              <div>
                <div className="dash-insight-label">Códigos gerados</div>
                <div className="dash-insight-value">{codes.length}</div>
                <div className="dash-insight-sub">no total</div>
              </div>
            </div>
            <div className="dash-insight">
              <div className="dash-insight-icon week">
                <i className="fas fa-calendar-week"></i>
              </div>
              <div>
                <div className="dash-insight-label">Esta semana</div>
                <div className="dash-insight-value">{codesThisWeek}</div>
                <div className="dash-insight-sub">códigos novos</div>
              </div>
            </div>
            {shopifyConnected && (
              <div
                className={`dash-insight ${shopifyPendingOrders > 0 ? 'shopify-pending' : 'shopify-ok'}`}
                onClick={() => navigate('/integrations/shopify')}
                role="button"
                tabIndex={0}
              >
                <div className={`dash-insight-icon ${shopifyPendingOrders > 0 ? 'shopify' : 'shopify-done'}`}>
                  <i className={`fab fa-shopify ${shopifyPendingOrders > 0 ? 'dash-icon-shake' : ''}`}></i>
                  {shopifyPendingOrders > 0 && <span className="dash-insight-dot"></span>}
                </div>
                <div>
                  <div className="dash-insight-label">Códigos para atualizar</div>
                  <div className="dash-insight-value">{shopifyPendingOrders}</div>
                  <div className="dash-insight-sub">
                    {shopifyPendingOrders === 0
                      ? <span className="dash-ok-sub"><i className="fas fa-check-circle"></i> tudo em dia</span>
                      : shopifyPendingOrders === 1 ? '1 pedido aguardando' : 'pedidos aguardando'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Cards de atalho "Gerar código"/"Comprar créditos" removidos —
              já ficam sempre visíveis na sidebar (ClientLayout), sem precisar
              duplicar aqui. */}

          {/* INTEGRAÇÕES */}
          <div className="dash-section-title">
            <i className="fas fa-plug"></i> Integrações
          </div>
          <div className="dash-integrations">
            <button
              className="dash-integration-card"
              onClick={() => navigate('/integrations/shopify')}
              aria-label="Integração com Shopify"
            >
              <div className="dash-integration-icon">
                <i className="fab fa-shopify"></i>
              </div>
              <div className="dash-integration-body">
                <div className="dash-integration-title">
                  Shopify
                  <span className={`dash-integration-badge ${shopifyConnected ? 'connected' : ''}`}>
                    {shopifyConnected ? 'Conectada' : 'Disponível'}
                  </span>
                </div>
                <div className="dash-integration-desc">
                  {shopifyConnected
                    ? 'Sua loja está conectada. Importe pedidos pagos e gere códigos automaticamente.'
                    : 'Conecte sua loja Shopify e gere rastreios automáticos para os pedidos pagos.'}
                </div>
              </div>
              <i className="fas fa-arrow-right dash-action-arrow"></i>
            </button>
          </div>

          {/* CÓDIGOS RECENTES */}
          <div className="dash-panel">
            <div className="dash-panel-head">
              <div className="dash-panel-title">
                <i className="fas fa-history"></i> Códigos recentes
              </div>
              {codes.length > 0 && (
                <button className="dash-panel-link" onClick={() => navigate('/my-codes')}>
                  Ver todos <i className="fas fa-arrow-right" style={{ fontSize: 11 }}></i>
                </button>
              )}
            </div>

            {loadingData ? (
              <div>
                <div className="dash-skel dash-skel-row" />
                <div className="dash-skel dash-skel-row" />
                <div className="dash-skel dash-skel-row" />
              </div>
            ) : recentCodes.length === 0 ? (
              <div className="dash-empty">
                <i className="fas fa-inbox"></i>
                <div className="dash-empty-text">Você ainda não gerou nenhum código.</div>
                <button className="dash-empty-cta" onClick={() => navigate('/generate-code')}>
                  Gerar meu primeiro código
                </button>
              </div>
            ) : (
              <div className="dash-codes-list">
                {recentCodes.map((c) => {
                  const route = getRoute(c.codigo);
                  const r = ROUTE_COLORS[route];
                  const isCopied = copied === c.codigo;
                  return (
                    <div key={c.id || c.codigo} className="dash-code-row">
                      <span
                        className="dash-code-route"
                        style={{ background: r.bg, color: r.color }}
                      >
                        {r.label}
                      </span>
                      <div className="dash-code-text">
                        <div className="dash-code-num">{c.codigo}</div>
                        <div className="dash-code-meta">
                          {c.destino ? `${c.destino} • ` : ''}{formatRelTime(c.created_at)}
                        </div>
                      </div>
                      <button
                        className={`dash-code-copy ${isCopied ? 'copied' : ''}`}
                        onClick={() => copyCode(c.codigo)}
                        title={isCopied ? 'Copiado!' : 'Copiar código'}
                      >
                        <i className={`fas ${isCopied ? 'fa-check' : 'fa-copy'}`}></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>

        {/* Modal de alterar senha removido daqui — agora vive no ClientLayout
            (components/ChangePasswordModal.js), acessível de qualquer tela. */}
      </div>
    </>
  );
};

export default Dashboard;
